import React, { useState, useRef } from "react";
import "../../pages/Dashboard.scss";
import icSidenav1 from "../../assets/images/ic_Home.svg";
import icSidenav2 from "../../assets/images/Ic_people.svg";
import icSidenav3 from "../../assets/images/ic_performance.svg";
import icToken from "../../assets/images/ic_tokens.svg";
import icFeedback from "../../assets/images/ic_feedback.svg";
import icUserRoles from "../../assets/images/ic_userroles.svg";
// import icSidenav4 from "../../assets/images/ic_FirstAID.svg";
// import icSidenav5 from "../../assets/images/ic_accessibilty.svg";
import icSidenav6 from "../../assets/images/ic_phone.svg";
import icSidenav7 from "../../assets/images/ic_procurement.svg";
import icChatHistory from "../../assets/images/ic-chathistory.svg";
import mainLogo from "../../assets/images/TheShelfMainLogo.svg";
import AssignmentIcon from "@mui/icons-material/Assignment";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
  Box,
} from "@mui/material";
import LogoutOutlinedIcon from "@mui/icons-material/Logout";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../store";
import { isAdminUser } from "../../utils/constants";

const CONTACT_POPOVER_CONTENT = (
  <>
    <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
      Please contact below for any queries:
    </Typography>
    <Typography variant="body2" component="div" sx={{ lineHeight: 1.8 }}>
      AI Foundry Team –{" "}
      <Box
        component="a"
        href="mailto:theshelf@g42.ai"
        sx={{ color: "primary.main" }}
      >
        theshelf@g42.ai
      </Box>
      <br />
      Khaled Al-Basti –{" "}
      <Box
        component="a"
        href="mailto:khaled.basti@inceptionai.ai"
        sx={{ color: "primary.main" }}
      >
        khaled.basti@inceptionai.ai
      </Box>
      <br />
      Hesham Mabrouk –{" "}
      <Box
        component="a"
        href="mailto:hesham.mabrouk@g42.ai"
        sx={{ color: "primary.main" }}
      >
        hesham.mabrouk@g42.ai
      </Box>
      <br />
      Akshay Goel –{" "}
      <Box
        component="a"
        href="mailto:akshay.goel@inceptionai.ai"
        sx={{ color: "primary.main" }}
      >
        akshay.goel@inceptionai.ai
      </Box>
    </Typography>
  </>
);

type SidebarProps = {
  activeNav?: string;
  onNavClick?: (navId: string) => void;
  user?: { name?: string; username?: string };
  onLogout?: () => void;
  onChatHistoryClick?: () => void;
  showChatHistory?: boolean;
  isChatHistoryOpen?: boolean;
};

export default function Sidebar({
  activeNav = "home",
  onNavClick,
  user,
  onLogout,
  onChatHistoryClick,
  showChatHistory = false,
  isChatHistoryOpen = false,
}: SidebarProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const allAgents = useSelector((state: RootState) => state.agents.data);
  const currentUserId = allAgents[0]?.user_id;

  // Fallback to localStorage if Redux state is not yet loaded
  const storedUserId =
    currentUserId !== undefined
      ? currentUserId
      : parseInt(localStorage.getItem("user_id") || "0", 10);

  const isAdmin = isAdminUser(storedUserId);

  const [popoverAnchorEl, setPopoverAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const popoverOpen = Boolean(popoverAnchorEl);

  const [dashboardSubmenuAnchorEl, setDashboardSubmenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const dashboardSubmenuOpen = Boolean(dashboardSubmenuAnchorEl);
  const dashboardSubmenuLeaveTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const dashboardSubmenuOpenedAtRef = useRef<number>(0);

  const HOVER_OPEN_GUARD_MS = 400;
  const HOVER_CLOSE_DELAY_MS = 200;
  const G42_GREEN = "#00d2aa";

  const handleContactPopoverToggle = (event: React.MouseEvent<HTMLElement>) => {
    setPopoverAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handlePopoverClose = () => setPopoverAnchorEl(null);

  const handleDashboardSubmenuClose = () => setDashboardSubmenuAnchorEl(null);

  const handleDashboardSubmenuMouseEnter = (
    event: React.MouseEvent<HTMLElement>,
  ) => {
    if (dashboardSubmenuLeaveTimerRef.current) {
      clearTimeout(dashboardSubmenuLeaveTimerRef.current);
      dashboardSubmenuLeaveTimerRef.current = null;
    }
    dashboardSubmenuOpenedAtRef.current = Date.now();
    setDashboardSubmenuAnchorEl(event.currentTarget);
  };

  const handleDashboardSubmenuMouseLeave = () => {
    if (Date.now() - dashboardSubmenuOpenedAtRef.current < HOVER_OPEN_GUARD_MS)
      return;
    dashboardSubmenuLeaveTimerRef.current = setTimeout(() => {
      setDashboardSubmenuAnchorEl(null);
      dashboardSubmenuLeaveTimerRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    onLogout?.();
    handleMenuClose();
  };

  const dashboardSubmenuItems = [
    {
      label: "Contract Performance",
      path: "/contract-performance",
      disabled: false,
    },
    {
      label: "Procurement Health",
      path: "/procurement-health",
      disabled: true,
    },
    { label: "Supplier Health", path: "/supplier-health", disabled: true },
    {
      label: "Sourcing & PR Efficiency",
      path: "/pr-efficiency",
      disabled: true,
    },
    {
      path: "/chat/procurement-insights",
      disabled: false,
    },
  ];

  const dashboardRoutes = dashboardSubmenuItems
    .filter((item) => !item.disabled)
    .map((item) => item.path);
  const isDashboardPage = dashboardRoutes.includes(location.pathname);
  const isProcurementInsightsPage = location.pathname.startsWith(
    "/chat/procurement-insights",
  );

  const isProcurementDashboardPage = location.pathname.startsWith(
    "/procurement-dashboard",
  );

  const effectiveActiveNav = isDashboardPage
    ? "dashboard"
    : isProcurementDashboardPage
      ? "procurement-dashboard"
      : activeNav;

  const navItems = [
    { id: "home", icon: icSidenav1 },
    ...(isProcurementInsightsPage || isProcurementDashboardPage
      ? [
          {
            id: "procurement-dashboard" as const,
            icon: null,
            muiIcon: AssignmentIcon,
          },
        ]
      : []),
    ...(isDashboardPage && !isProcurementInsightsPage
      ? [{ id: "dashboard" as const, icon: icSidenav7 }]
      : []),
    { id: "analytics", icon: icSidenav2 },
    { id: "search", icon: icSidenav3 },
    { id: "token", icon: icToken },
    { id: "feedback", icon: icFeedback },
    ...(isAdmin ? [{ id: "user-management", icon: icUserRoles }] : []),
    ...(showChatHistory && !isChatHistoryOpen
      ? [{ id: "chat-history" as const, icon: icChatHistory }]
      : []),
    { id: "notifications", icon: icSidenav6 },
  ];

  const userInitial =
    user?.name?.trim().charAt(0).toUpperCase() ||
    user?.username?.trim().charAt(0).toUpperCase() ||
    "U";

  return (
    <aside className="sidebar">
      <IconButton
        onClick={() => navigate("/landing")}
        className="sidebar-logo"
        aria-label="Go to landing page"
      >
        <img src={mainLogo} alt="G42 Logo" width={40} height={40} />
      </IconButton>

      <nav className="nav">
        {navItems.map((item) => {
          const isContactPopoverIcon = item.id === "notifications";
          const isDashboard = item.id === "dashboard";
          const isChatHistory = item.id === "chat-history";
          const showDashboardSubmenu =
            isDashboardPage && isDashboard && dashboardSubmenuOpen;
          const MuiIcon = (item as any).muiIcon;

          return (
            <React.Fragment key={item.id}>
              <button
                className={`nav-btn ${effectiveActiveNav === item.id ? "active" : ""} ${
                  isChatHistory ? "chat-history-btn" : ""
                } ${isChatHistory && isChatHistoryOpen ? "chat-history-open" : ""}`}
                onClick={(e) => {
                  if (isContactPopoverIcon) {
                    handleContactPopoverToggle(e);
                  } else if (isChatHistory) {
                    onChatHistoryClick?.();
                  } else if (isDashboard) {
                    // optional: keep current page or navigate
                  } else if (onNavClick) {
                    onNavClick(item.id);
                  } else {
                    if (item.id === "home") {
                      navigate("/landing");
                    } else if (item.id === "procurement-dashboard") {
                      navigate("/procurement-dashboard/contracts-management");
                    } else if (item.id === "dashboard") {
                      navigate("/supplier-health");
                    } else if (item.id === "analytics") {
                      navigate("/onboarding");
                    } else if (item.id === "search") {
                      navigate("/performance");
                    } else if (item.id === "token") {
                      navigate("/tokenconsumption");
                    } else if (item.id === "feedback") {
                      navigate("/feedback");
                    } else if (item.id === "user-management") {
                      navigate("/user-management");
                    }
                  }
                }}
                onMouseEnter={
                  isDashboard && isDashboardPage
                    ? handleDashboardSubmenuMouseEnter
                    : undefined
                }
                onMouseLeave={
                  isDashboard && isDashboardPage
                    ? handleDashboardSubmenuMouseLeave
                    : undefined
                }
                aria-label={
                  item.id === "feedback"
                    ? "Feedback"
                    : item.id === "chat-history"
                      ? "Chat History"
                      : item.id === "procurement-dashboard"
                        ? "Procurement Dashboard"
                        : item.id
                }
                aria-haspopup={
                  isContactPopoverIcon || (isDashboard && isDashboardPage)
                    ? "true"
                    : undefined
                }
                aria-expanded={
                  (isContactPopoverIcon && popoverOpen) || showDashboardSubmenu
                    ? "true"
                    : undefined
                }
              >
                {MuiIcon ? (
                  <MuiIcon className="nav-icon" sx={{ fontSize: 28 }} />
                ) : (
                  <img
                    src={(item as any).icon}
                    alt={item.id}
                    className="nav-icon"
                  />
                )}
              </button>
            </React.Fragment>
          );
        })}
      </nav>

      <Popover
        open={popoverOpen}
        anchorEl={popoverAnchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        disableEnforceFocus
        disableRestoreFocus
        sx={{ ml: 1.5 }}
      >
        <Box sx={{ position: "relative", p: 2, minWidth: 280, maxWidth: 320 }}>
          <IconButton
            size="small"
            onClick={handlePopoverClose}
            aria-label="Close"
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {CONTACT_POPOVER_CONTENT}
        </Box>
      </Popover>

      {isDashboardPage && (
        <Popover
          open={dashboardSubmenuOpen}
          anchorEl={dashboardSubmenuAnchorEl}
          onClose={handleDashboardSubmenuClose}
          anchorOrigin={{ vertical: "center", horizontal: "right" }}
          transformOrigin={{ vertical: "center", horizontal: "left" }}
          disableEnforceFocus
          disableRestoreFocus
          PaperProps={{
            onMouseEnter: () => {
              if (dashboardSubmenuLeaveTimerRef.current) {
                clearTimeout(dashboardSubmenuLeaveTimerRef.current);
                dashboardSubmenuLeaveTimerRef.current = null;
              }
            },
            onMouseLeave: handleDashboardSubmenuMouseLeave,
          }}
          sx={{ ml: 1.5 }}
        >
          <Box sx={{ py: 0.5, minWidth: 160 }}>
            {dashboardSubmenuItems.map((item) => {
              const isSelected = location.pathname === item.path;
              const isDisabled = item.disabled || false;
              return (
                <MenuItem
                  key={item.path}
                  selected={isSelected && !isDisabled}
                  disabled={isDisabled}
                  onClick={() => {
                    if (!isDisabled) {
                      navigate(item.path);
                      handleDashboardSubmenuClose();
                    }
                  }}
                  sx={{
                    fontSize: 14,
                    py: 1.25,
                    px: 2,
                    ...(isDisabled && {
                      color: "#9e9e9e",
                      cursor: "not-allowed",
                      opacity: 0.6,
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }),
                    ...(isSelected &&
                      !isDisabled && {
                        color: G42_GREEN,
                        fontWeight: 600,
                        backgroundColor: "rgba(0, 210, 170, 0.08)",
                      }),
                  }}
                >
                  <ListItemText primary={item.label} />
                </MenuItem>
              );
            })}
          </Box>
        </Popover>
      )}

      <div className="sidebar-profile">
        <IconButton
          onClick={handleMenuOpen}
          className="sidebar-profile-btn"
          aria-controls={open ? "sidebar-user-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <div
            className="avatar avatar-initial"
            style={{ fontFamily: "G42 Sans" }}
          >
            {userInitial}
          </div>
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
      </div>
    </aside>
  );
}
